<script setup>
import {computed, onBeforeMount, onBeforeUnmount} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'

import TemplatesList from './TemplatesList.vue'
import TemplatesLibraryList from './TemplatesLibraryList.vue'
import {Button} from '@/components/ui/button'
import {PlusCircle} from 'lucide-vue-next'
import CreateTemplateDialog from '@/pages/Project/Templates/CreateTemplateDialog.vue'
import { useCheckProjectAccess } from '@/composables/useCheckAccess'

const route = useRoute()
const projectId = route.params.project_id

const store = useStore()
const project = computed(() => store.getters['project/current'])
const templates = computed(() => {
  const allTemplates = store.getters['geo/getProjectReportTemplates']
  if (!allTemplates) return null
  return [...allTemplates].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})
const templatesLength = computed(() => templates.value ? templates.value.length : 0)

const libraryTemplates = computed(() => {
  const library = store.getters['geo/getSystemTemplatesLibrary']
  if (!library) return null
  return [...library].sort((a, b) => a.name.localeCompare(b.name))
})
const libraryLength = computed(() => libraryTemplates.value ? libraryTemplates.value.length : 0)

onBeforeMount(async () => {
  await store.dispatch('project/fetchProject', projectId)
  await store.dispatch('geo/fetchProjectReportTemplates', projectId)
  await store.dispatch('geo/fetchSystemTemplatesLibrary', projectId)
})

onBeforeUnmount(() => {
  store.commit('geo/SET_REPORT_TEMPLATES', null)
  store.commit('geo/SET_SYSTEM_TEMPLATES_LIBRARY', null)
})
</script>

<template>
  <div class="mb-1 flex items-center">
    <div class="basis-1/2">
      <h3 class="text-base font-bold">Report Templates ({{templatesLength}})</h3>
    </div>
    <div class="basis-1/2 flex justify-end">
      <CreateTemplateDialog :project-id="Number(projectId)">
        <Button
          class="px-2 text-muted-foreground max-w-fit"
          variant="ghost"
          data-testid="create-template-button"
        ><PlusCircle /></Button>
      </CreateTemplateDialog>
    </div>
  </div>
  <TemplatesList v-if="templates" :project="project" :templates="templates" :project-id="Number(projectId)" />

  <!-- Templates Library Section -->
  <div class="mt-8 mb-1 flex items-center">
    <div class="basis-1/2 flex items-center gap-2">
      <h3 class="text-base font-bold">Templates Library ({{libraryLength}})</h3>
    </div>
  </div>
  <p class="text-sm text-muted-foreground mb-3">System templates available to add to your project</p>
  <TemplatesLibraryList
    v-if="libraryTemplates && libraryTemplates.length > 0"
    :templates="libraryTemplates"
    :project-id="Number(projectId)"
  />
  <div v-else-if="libraryTemplates && libraryTemplates.length === 0" class="text-center py-8 text-muted-foreground">
    No system templates available
  </div>
</template>
